import React, { useEffect, useState } from 'react';
import {
    Tabs,
    Table,
    Button,
    Space,
    Tag,
    Spin,
    Alert,
    message,
} from 'antd';
import {
    CheckOutlined,
    UndoOutlined,
    EditOutlined,
} from '@ant-design/icons';
import axios from 'axios';

// Aligning with your Timesheet manager architecture
import { baseUrl_SPB } from '../../BaseUrl';
import { useAuth } from '../../AuthProvider';
import AddOrRetrieveLeaveModal from './AddLeaveModal'; // Your existing leave modal adapted for Web
import LeaveHoursReport from './LeaveHoursReport';

const LeaveManager = () => {
    const authContext = useAuth();
    const [loading, setLoading] = useState(false);
    const [leaves, setLeaves] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const token = authContext.token;
    const companyId = authContext.additionalUserInfo?.companyId;
    const isUserManager = authContext.role === 'ROLE_MANAGER' || authContext.role === 'ROLE_CMANAGER';
    const currentUserId = authContext.additionalUserInfo?.id;

    useEffect(() => {
        if (token && companyId) {
            fetchLeaves();
        }
    }, [token, companyId]);

    // Replacing FetchAllLeavesByCompany with an Axios request pattern
    const fetchLeaves = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${baseUrl_SPB}/api/v1/leave/fetchleavesbycid/${companyId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            let data = response.data || [];
            
            // Filter leaves if the current user is an employee rather than a manager
            if (!isUserManager) {
                data = data.filter((x) => x.staffId === currentUserId);
            }
           

            setLeaves(data);
        } catch (err) {
            console.error(err);
            message.error('Failed to load leaves.');
        } finally {
            setLoading(false);
        }
    };

    // Single approval / undo state updates
    const updateApprovalStatus = async (item, status) => {
        try {
            setLoading(true);
            const modItem = { ...item, approvalStatus: status };

            await axios.post(
                `${baseUrl_SPB}/api/v1/leave/updateleavestatus`,
                [modItem],
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setLeaves((prevLeaves) =>
                prevLeaves.map((leave) =>
                    leave.id === item.id ? { ...leave, approvalStatus: status } : leave
                )
            );

            message.success('Leave status updated successfully.');
        } catch (err) {
            console.error(err);
            message.error('Failed to update leave status.');
        } finally {
            setLoading(false);
        }
    };

    // Bulk Approval functionality matching your Timesheet manager layout
    const approveSelected = async () => {
        try {
            setLoading(true);
            const selectedItems = pendingLeaves.filter((x) =>
                selectedRowKeys.includes(x.id)
            );

            await axios.post(
                `${baseUrl_SPB}/api/v1/leave/updateleavestatus`,
                selectedItems.map((x) => ({
                    ...x,
                    approvalStatus: 'approved',
                })),
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setLeaves((prev) =>
                prev.map((x) =>
                    selectedRowKeys.includes(x.id)
                        ? { ...x, approvalStatus: 'approved' }
                        : x
                )
            );

            setSelectedRowKeys([]);
            message.success(`${selectedItems.length} leaves approved.`);
        } catch (err) {
            console.error(err);
            message.error('Bulk approval failed.');
        } finally {
            setLoading(false);
        }
    };

    // Local filtering derived cleanly from state
    const pendingLeaves = leaves.filter(
        (x) => !x.approvalStatus || x.approvalStatus === '' || x.approvalStatus.toLowerCase() === 'pending'
    );

    const approvedLeaves = leaves.filter(
        (x) => x.approvalStatus?.toLowerCase() === 'approved'
    );

    // Column generator structural mirror
    const columns = (isPending) => [
        {
            title: 'Employee',
            dataIndex: 'staffName',
        },
        {
            title: 'Date Range',
            render: (_, record) => `${record.startDate} - ${record.endDate}`,
        },
        {
            title: 'Time Window',
            render: (_, record) => `(${record.starttime} - ${record.endtime})`,
        },
        {
            title: 'Status',
            dataIndex: 'approvalStatus',
            render: (status) => (
                <Tag color={status === 'approved' ? 'green' : 'orange'}>
                    {status ? status.toUpperCase() : 'PENDING'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => {
                            setSelectedItem(record);
                            setModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>

                    {isUserManager &&
                        (isPending ? (
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                size="small"
                                onClick={() => updateApprovalStatus(record, 'approved')}
                            >
                                Approve
                            </Button>
                        ) : (
                            <Button
                                danger
                                icon={<UndoOutlined />}
                                size="small"
                                onClick={() => updateApprovalStatus(record, '')}
                            >
                                Undo
                            </Button>
                        ))}
                </Space>
            ),
        },
    ];

    return (
        <Spin spinning={loading}>
            <Tabs
                defaultActiveKey="pending"
                items={[
                    {
                        key: 'pending',
                        label: 'Pending Leaves',
                        children: (
                            <>
                                <Space style={{ marginBottom: 16 }}>
                                    <Button
                                        onClick={() =>
                                            setSelectedRowKeys(pendingLeaves.map((x) => x.id))
                                        }
                                    >
                                        Select All
                                    </Button>

                                    <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>

                                    {selectedRowKeys.length > 0 && isUserManager && (
                                        <Button type="primary" onClick={approveSelected}>
                                            Approve Selected
                                        </Button>
                                    )}
                                </Space>

                                <Table
                                    rowKey="id"
                                    dataSource={pendingLeaves}
                                    columns={columns(true)}
                                    rowSelection={{
                                        selectedRowKeys,
                                        onChange: setSelectedRowKeys,
                                    }}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'approved',
                        label: 'Approved Leaves',
                        children: (
                            <Table
                                rowKey="id"
                                dataSource={approvedLeaves}
                                columns={columns(false)}
                            />
                        ),
                    },
                    {
                        key: 'reports',
                        label: 'Hours report',
                        children: (
                            <LeaveHoursReport
                                leaves={leaves}
                            />
                        )
                    },
                ]}
            />

            <AddOrRetrieveLeaveModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedItem(null);
                }}
                onSave={() => {
                    fetchLeaves();
                    setModalOpen(false);
                }}
                token={token}
                resource={{
                    id: selectedItem?.staffId,
                }}
                selectedItem={selectedItem}
                isUserManager={isUserManager}
                companyId={companyId}
            />
        </Spin>
    );
};

export default LeaveManager;